import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigation } from '../shared/hooks/useNavigation'
import { ROUTES } from '../shared/navigation'
import { Search, Plus, Users, Phone, Mail, ChevronRight, UserX, Loader2, RefreshCw, CreditCard, MapPin } from '../components/icons'
import { clientService } from '../services/clientService'
import { useAuthStore } from '../stores/authStore'
import { useViewModeStore } from '../stores/viewModeStore'
import ProfessionalBottomNavigation from '../components/ProfessionalBottomNavigation'
import { formatCpf, formatAddressCompact } from '../utils/brazilianUtils'

const ClientsPage = () => {
  const { navigate } = useNavigation()
  const { hasRole } = useAuthStore()
  const { isProfessionalMode } = useViewModeStore()
  
  // State management
  const [clients, setClients] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState('')
  const [totalClients, setTotalClients] = useState(0)
  
  // Pagination
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 20

  // Check if user can manage clients
  const canManageClients = hasRole(['GESTOR', 'ATENDENTE'])
  const canDeleteClients = hasRole(['GESTOR'])

  // Optimized search function - no debounce, 3 char minimum
  const performSearch = useMemo(() => {
    return (term) => {
      // Only search if 3+ characters (never show all)
      if (term.trim().length >= 3) {
        loadClients(true, term)
      } else if (term.trim().length === 0) {
        // Clear results when search is empty
        setClients([])
        setTotalClients(0)
        setPage(0)
        setHasMore(false)
      }
    }
  }, [])

  // Load clients function
  const loadClients = useCallback(async (reset = false, search = searchTerm) => {
    try {
      const isFirstLoad = reset || page === 0
      setIsLoading(isFirstLoad)
      setIsRefreshing(!isFirstLoad)
      setError('')

      const currentPage = reset ? 0 : page
      const response = await clientService.getClients({
        search: search.trim(),
        skip: currentPage * PAGE_SIZE,
        limit: PAGE_SIZE
      })

      if (reset) {
        setClients(response.items)
        setPage(1)
      } else {
        setClients(prev => [...prev, ...response.items])
        setPage(prev => prev + 1)
      }

      setHasMore(response.has_more)
      setTotalClients(response.total)
    } catch (error) {
      setError('Erro ao carregar clientes. Tente novamente.')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [page, searchTerm])

  // No initial load - start with empty state like Web-admin
  // useEffect(() => {
  //   loadClients(true)
  // }, [])

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term)
    setPage(0)
    performSearch(term)
  }

  // Handle refresh
  const handleRefresh = () => {
    setPage(0)
    // Only refresh if there's a search term (3+ chars)
    if (searchTerm.trim().length >= 3) {
      loadClients(true)
    } else {
      // Clear everything if no valid search
      setClients([])
      setTotalClients(0)
      setHasMore(false)
    }
  }

  // Load more (infinite scroll)
  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      loadClients(false)
    }
  }

  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return ''
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  // Get client initials for avatar
  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  // Client card component - Luxury Design
  const ClientCard = ({ client }) => (
    <div
      className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4 mb-3 active:bg-brand-dark/5 dark:active:bg-brand-light/5 transition-all duration-300"
      onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENT_DETAIL(client.id))}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          {/* Avatar - Square */}
          <div className="w-12 h-12 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-4 flex-shrink-0">
            <span className="text-brand-dark dark:text-brand-light font-semibold text-sm tracking-wide">
              {getInitials(client.full_name)}
            </span>
          </div>

          {/* Client Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light text-base truncate tracking-wide">
              {client.full_name || 'Nome não informado'}
            </h3>

            <div className="flex items-center mt-1 text-sm text-brand-dark/60 dark:text-brand-light/60">
              <Mail size={14} className="mr-1 flex-shrink-0" strokeWidth={1.5} />
              <span className="truncate tracking-wide">{client.email}</span>
            </div>

            {client.phone_number && (
              <div className="flex items-center mt-1 text-sm text-brand-dark/60 dark:text-brand-light/60">
                <Phone size={14} className="mr-1 flex-shrink-0" strokeWidth={1.5} />
                <span className="tracking-wide">{formatPhone(client.phone_number)}</span>
              </div>
            )}

            {client.cpf && (
              <div className="flex items-center mt-1 text-sm text-brand-dark/60 dark:text-brand-light/60">
                <CreditCard size={14} className="mr-1 flex-shrink-0" strokeWidth={1.5} />
                <span className="tracking-wide">{formatCpf(client.cpf)}</span>
              </div>
            )}

            {(client.address_street || client.address_city) && (
              <div className="flex items-center mt-1 text-sm text-brand-dark/60 dark:text-brand-light/60">
                <MapPin size={14} className="mr-1 flex-shrink-0" strokeWidth={1.5} />
                <span className="truncate tracking-wide">{formatAddressCompact(client)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ChevronRight size={20} className="text-brand-dark/40 dark:text-brand-light/40 ml-3" strokeWidth={1.5} />
      </div>

      {/* Status indicator - Square */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-brand-dark/10 dark:border-brand-light/10">
        <div className="flex items-center">
          <div className={`w-2 h-2 mr-2 ${client.is_active ? 'bg-green-600 dark:bg-green-400' : 'bg-brand-dark/40 dark:bg-brand-light/40'}`} />
          <span className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">
            {client.is_active ? 'Ativo' : 'Inativo'}
          </span>
        </div>

      </div>
    </div>
  )

  // Empty state - Luxury Design
  const EmptyState = () => {
    // If there's a search term, show "not found" message
    if (searchTerm.trim().length >= 3) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-16 h-16 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mb-4">
            <Users size={32} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">
            Nenhum cliente encontrado
          </h3>
          <p className="text-brand-dark/60 dark:text-brand-light/60 text-center mb-6 tracking-wide">
            Nenhum cliente encontrado para &quot;{searchTerm}&quot;
          </p>
          <p className="text-brand-dark/50 dark:text-brand-light/50 text-center text-sm tracking-wide">
            Tente buscar por nome, email ou telefone
          </p>
        </div>
      )
    }

    // Default empty state - search prompt
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-16 h-16 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mb-4">
          <Search size={32} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
        </div>
        <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">
          Buscar Clientes
        </h3>
        <p className="text-brand-dark/60 dark:text-brand-light/60 text-center mb-6 tracking-wide">
          Digite pelo menos 3 caracteres no campo de busca para encontrar clientes
        </p>
        <p className="text-brand-dark/50 dark:text-brand-light/50 text-center text-sm tracking-wide">
          Busque por nome, email ou telefone
        </p>
        {canManageClients && (
          <button
            onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENT_CREATE)}
            className="bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light px-6 py-3 font-medium uppercase tracking-widest text-sm mt-4 hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
          >
            Adicionar Novo Cliente
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Header - Luxury Design */}
      <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Clientes</h1>
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
              {totalClients > 0 ? `${totalClients} ${totalClients === 1 ? 'cliente' : 'clientes'}` : 'Gerencie seus clientes'}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent disabled:opacity-50 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
            >
              <RefreshCw size={20} strokeWidth={1.5} className={`text-brand-dark dark:text-brand-light ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>

            {/* Add client button */}
            {canManageClients && (
              <button
                onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENT_CREATE)}
                className="bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light p-2 hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
              >
                <Plus size={20} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        {/* Search bar - Luxury Design */}
        <div className="relative">
          <Search size={20} strokeWidth={1.5} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" />
          <input
            type="text"
            placeholder="Digite pelo menos 3 caracteres para buscar..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light focus:border-brand-dark dark:focus:border-brand-light transition-all duration-300 tracking-wide"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto mobile-scroll">
        {error && (
          <div className="mx-4 mt-4 p-4 bg-transparent border border-red-600/30 dark:border-red-400/30">
            <p className="text-red-600 dark:text-red-400 text-sm text-center tracking-wide">{error}</p>
          </div>
        )}

        {isLoading && clients.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin"></div>
          </div>
        ) : clients.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="px-4 py-4">
            {clients.map((client) => (
              <ClientCard key={client.id} client={client} />
            ))}

            {/* Load more button */}
            {hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light px-6 py-3 font-medium uppercase tracking-widest text-sm disabled:opacity-50 flex items-center hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin mr-2"></div>
                  ) : null}
                  {isLoading ? 'Carregando...' : 'Carregar mais'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Bottom Navigation - Only show in professional mode */}
      {isProfessionalMode() && <ProfessionalBottomNavigation />}
    </div>
  )
}

export default ClientsPage